/* src/net/supabaseClient.ts — the one Supabase client instance.
 *
 * Identity is a Supabase anonymous-auth JWT, not a client-minted UUID (PLAN.md Part 3 §8,
 * CLAUDE.md Security). The original passes a raw player UUID in the URL path; UUIDs are not
 * secrets, so that design lets anyone who learns an id read — and potentially forge writes
 * as — that player. We do not copy it. `auth.uid()` from the JWT's `sub` claim is the only
 * identity the server ever trusts.
 *
 * The client is created lazily and returns `null` when `.env` has no Supabase config, so the
 * app boots and renders without credentials instead of throwing at module load.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { SUPABASE_ANON_KEY, SUPABASE_URL, isSupabaseConfigured } from '../core/env.ts';

let client: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient | null {
  if (!isSupabaseConfigured) return null;
  client ??= createClient(SUPABASE_URL as string, SUPABASE_ANON_KEY as string, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // No OAuth redirect flow — anonymous sign-in only, so there is no URL fragment to parse.
      detectSessionInUrl: false,
    },
  });
  return client;
}

/** The current access token, or null when signed out / unconfigured. Never logged. */
export async function getAccessToken(): Promise<string | null> {
  const supabase = getSupabase();
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/** Test seam — drops the memoised client so a test can swap env. */
export function resetSupabaseClient(): void {
  client = null;
}
